import type { URLOpenListenerEvent } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { useEffect } from "react";
import { assignLocation } from "./assignLocation";

const appModule =
	import.meta.env.VITE_CAPACITOR_BUILD === "true"
		? import("@capacitor/app")
		: null;

const HANDLED_LAUNCH_URL_KEY = "deepLinkHandledLaunchUrl";

export default function DeepLinkListener() {
	useEffect(() => {
		let listenerHandle: PluginListenerHandle;
		appModule?.then((m) => {
			m.App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
				// iOS getLaunchUrl returns the most recently opened url, so record it
				// to keep the launch url fallback from redirecting to it on reload.
				sessionStorage.setItem(HANDLED_LAUNCH_URL_KEY, event.url);
				assignLocation(toAppPath(event.url));
			}).then((handle) => {
				listenerHandle = handle;
			});

			// On cold start appUrlOpen can fire before this listener registers, so
			// fall back to the url the app was launched with.
			m.App.getLaunchUrl().then((result) => {
				const launchUrl = result?.url;
				if (!launchUrl) return;
				// getLaunchUrl keeps returning the same url for the life of the process,
				// so without this guard a reload or later navigation would bounce back to it.
				if (sessionStorage.getItem(HANDLED_LAUNCH_URL_KEY) === launchUrl)
					return;
				sessionStorage.setItem(HANDLED_LAUNCH_URL_KEY, launchUrl);
				const appPath = toAppPath(launchUrl);
				if (appPath !== toAppPath(window.location.href)) {
					assignLocation(appPath);
				}
			});
		});

		return () => {
			listenerHandle?.remove();
		};
	}, []);

	return null;
}

function toAppPath(url: string): string {
	const parsed = new URL(url);
	return `${parsed.pathname}?${parsed.searchParams.toString()}`;
}
